/**
 * ExoPanelStatusIndicator
 * 
 * UX Principle #12: Silence Is Valid State
 * 
 * If no action is required, the Exo Panel must say so explicitly.
 * 
 * Examples:
 * - "No action required."
 * - "Waiting on upstream completion."
 * - "System stable."
 * 
 * Do not fill silence with nudges, filler work, or suggestions.
 * Idle does not mean broken. It means coordinated.
 */

import React from 'react';
import { ExoPanelStatus } from '../../types/executionSignal';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';

interface ExoPanelStatusIndicatorProps {
  status: ExoPanelStatus;
  compact?: boolean;
}

const STATUS_CONFIG = {
  no_action_required: {
    icon: <CheckCircleOutlined style={{ fontSize: '48px', color: '#10b981' }} />,
    title: 'No action required',
    color: '#10b981',
    bgColor: '#ecfdf5',
    borderColor: '#a7f3d0'
  },
  waiting_upstream: {
    icon: <ClockCircleOutlined style={{ fontSize: '48px', color: '#f59e0b' }} />,
    title: 'Waiting on upstream',
    color: '#f59e0b',
    bgColor: '#fffbeb',
    borderColor: '#fcd34d'
  },
  signals_pending: {
    icon: <SyncOutlined spin style={{ fontSize: '48px', color: '#3b82f6' }} />,
    title: 'Processing',
    color: '#3b82f6',
    bgColor: '#eff6ff',
    borderColor: '#93c5fd'
  },
  system_stable: {
    icon: <SafetyCertificateOutlined style={{ fontSize: '48px', color: '#6b7280' }} />,
    title: 'System stable',
    color: '#6b7280',
    bgColor: '#f9fafb',
    borderColor: '#e5e7eb'
  }
};

export const ExoPanelStatusIndicator: React.FC<ExoPanelStatusIndicatorProps> = ({
  status,
  compact = false
}) => {
  const config = STATUS_CONFIG[status.status];

  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        backgroundColor: config.bgColor,
        borderRadius: '8px',
        border: `1px solid ${config.borderColor}`
      }}>
        <div style={{ fontSize: '24px' }}>
          {React.cloneElement(config.icon as React.ReactElement, { 
            style: { fontSize: '24px', color: config.color } 
          })}
        </div>
        <div>
          <div style={{
            fontSize: '14px',
            fontWeight: 600,
            color: config.color
          }}>
            {status.message}
          </div>
          {status.waiting_on.length > 0 && (
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '2px'
            }}>
              {status.waiting_on.join(', ')}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 24px',
      backgroundColor: config.bgColor,
      borderRadius: '12px',
      border: `1px solid ${config.borderColor}`,
      textAlign: 'center'
    }}>
      {/* Icon */}
      <div style={{ marginBottom: '16px' }}>
        {config.icon}
      </div>

      {/* Title */}
      <h4 style={{
        margin: 0,
        marginBottom: '8px',
        fontSize: '20px',
        fontWeight: 600,
        color: config.color
      }}>
        {status.message}
      </h4>

      {/* Waiting on list */}
      {status.waiting_on.length > 0 && (
        <div style={{
          marginTop: '12px',
          padding: '12px 16px',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          borderRadius: '8px',
          maxWidth: '300px'
        }}>
          <div style={{
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: '#6b7280',
            marginBottom: '8px'
          }}>
            Waiting on
          </div>
          {status.waiting_on.map((item, index) => (
            <div
              key={index}
              style={{
                fontSize: '13px',
                color: '#374151',
                padding: '4px 0',
                borderTop: index > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none'
              }}
            >
              {item}
            </div>
          ))}
        </div>
      )}

      {/* Next expected */}
      {status.next_expected_at && (
        <div style={{
          marginTop: '16px',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          Next expected: {new Date(status.next_expected_at).toLocaleString()}
        </div>
      )}

      {/* Calm reassurance - per UX Principle #12 */}
      <div style={{
        marginTop: '24px',
        fontSize: '13px',
        color: '#9ca3af',
        fontStyle: 'italic'
      }}>
        {status.status === 'no_action_required' && 'All work is on track.'}
        {status.status === 'waiting_upstream' && 'Work will resume when dependencies complete.'}
        {status.status === 'system_stable' && 'Everything is coordinated.'}
      </div>
    </div>
  );
};
